import { useState, useRef, useEffect, useCallback } from 'react';
import { Printer, Loader2 } from 'lucide-react';
import { useToast } from '@mustauto/ui/web';
import { api } from '@/lib/api';
import { formatApiError } from '@mustauto/utils';

export interface PrintOSButtonProps {
  osId: string;
  osNumber: number;
}

/**
 * Imprime a OS a partir do PDF gerado pela API (`/service-orders/:id/print`).
 *
 * As observações internas são opcionais e vêm desmarcadas: a folha impressa
 * costuma terminar na mão do cliente, então incluí-las é escolha explícita de
 * quem está imprimindo, nunca o padrão (Regra 7).
 *
 * A aba é aberta *antes* do await, ainda dentro do gesto do usuário — abrir
 * depois cai no bloqueador de pop-up do Chrome e o clique não faz nada. O PDF
 * abre no visor do navegador, onde o Ctrl+P/botão de imprimir já está à mão.
 */
export function PrintOSButton({ osId, osNumber }: PrintOSButtonProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [withInternalNotes, setWithInternalNotes] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handlePrint = useCallback(async () => {
    setOpen(false);
    if (loading) return;
    setLoading(true);

    const printWindow = window.open('', '_blank');

    try {
      const res = await api.get(`/service-orders/${osId}/print`, {
        params: { withInternalNotes: withInternalNotes ? 'true' : 'false' },
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(
        new Blob([res.data], { type: 'application/pdf' }),
      );

      if (printWindow) {
        printWindow.location.href = blobUrl;
      } else {
        // Pop-up bloqueado: cai pro download, que nunca é bloqueado.
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `os-${osNumber}.pdf`;
        a.click();
      }

      // O visor precisa carregar o blob antes do revoke; 60s cobre folga.
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      printWindow?.close();
      console.error('[ServiceOrderDetail] print failed', err);
      toast({
        type: 'error',
        title: 'Falha ao gerar a impressão',
        description: formatApiError(
          err,
          'Não foi possível gerar o PDF da OS. Tente novamente em instantes.',
        ),
      });
    } finally {
      setLoading(false);
    }
  }, [osId, osNumber, withInternalNotes, loading, toast]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={loading}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={`Imprimir OS #${osNumber}`}
        className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium hover:bg-muted disabled:opacity-60"
      >
        {loading ? (
          <Loader2 size={15} className="animate-spin" />
        ) : (
          <Printer size={15} />
        )}
        Imprimir
      </button>

      {open && (
        <div className="absolute right-0 top-full z-20 mt-1 w-72 rounded-lg border border-border bg-card p-3 shadow-lg">
          <p className="text-sm font-medium">Imprimir OS #{osNumber}</p>

          <label className="mt-3 flex cursor-pointer items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={withInternalNotes}
              onChange={(e) => setWithInternalNotes(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-primary"
            />
            <span>
              Incluir observações internas
              <span className="block text-xs text-muted-foreground">
                Só marque se a folha não for para o cliente
              </span>
            </span>
          </label>

          <button
            type="button"
            onClick={handlePrint}
            className="mt-3 w-full rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Imprimir
          </button>
        </div>
      )}
    </div>
  );
}
